import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, HostBinding, computed, input, output, viewChild } from '@angular/core';
import { FilterDropdownOption } from './models/filter-dropdown-option.interface';

/**
 * A single-select filter styled to sit beside the `app-filter-bar` pills: the same
 * height, border and weight, and the selected pill's dark outline once something
 * other than the All option is chosen, so a set filter shows at a glance.
 *
 * A native `<select>`, so keyboard, touch and screen-reader behaviour come from
 * the platform. Its text is 16px at every width, because iOS Safari zooms the page
 * when a smaller form control is focused.
 *
 * A caption sits above the pill, so a set filter still says what it filters by —
 * the pill alone shows only the chosen option's name.
 */
@Component({
  selector: 'app-filter-dropdown',
  templateUrl: './filter-dropdown.component.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class FilterDropdownComponent {
  /** Full width below `sm`, so filters stack under the pills on a phone; content width from `sm` up. */
  @HostBinding('class') readonly hostClass = 'tw-relative tw-block tw-w-full sm:tw-w-auto';

  /** The options, the All option first. */
  readonly options = input.required<FilterDropdownOption[]>();
  /** The selected option's value; the empty string selects the All option. */
  readonly value = input<string>('');
  /**
   * The short noun shown above the pill, naming what the filter filters by ("Event").
   * Hidden from screen readers, which hear `label` instead, so write `label` to contain it.
   */
  readonly caption = input.required<string>();
  /** Names the control for screen readers, which the selected option alone doesn't. */
  readonly label = input.required<string>();
  /** Emits the chosen option's value. */
  readonly optionSelected = output<string>();

  /** The option `value` selects, or undefined when it names none — the select then shows the first. */
  private readonly _selectedOption = computed(() => this.options().find(option => option.value === this.value()));
  /** Whether an option other than All is selected. A value no option carries reads as unset, as it displays. */
  readonly isSet = computed(() => !!this._selectedOption()?.value);

  private readonly _select = viewChild.required<ElementRef<HTMLSelectElement>>('select');

  /** Moves keyboard focus to the control. */
  focus(): void {
    this._select().nativeElement.focus();
  }
}
